// voiceConfirmGate.js — Two-Man Rule for destructive voice actions.
// (c) 2026-09-05 — Hermes/Kara blueprint §12 implementation.
//
// WHY: When Hermes (LLM) mis-transcribes or the user misspeaks while driving,
// a single voice note could currently fire: execute_trade, activate_strategy,
// clear_symbols, clear_history, create_strategy — all marked "destructive" in
// the action registry but never gated. This module enforces a confirmation
// handshake on those actions.
//
// FLOW:
//   1. executeAction() (in voiceActions.js) calls gate.check(act).
//   2. Non-destructive actions proceed; destructive actions return needsConfirm.
//   3. AgentTab / AmberScanner retain the original action object while showing
//      the confirmation UI. Confirm approves that exact pending object; cancel
//      rejects it. Stale UI events cannot approve or clear a newer request.
//   4. The UI re-dispatches the confirmed action with __confirmed = true.
//   5. The approval is one-shot (30s TTL); unconfirmed pending requests expire
//      after 60s. No trade/action proceeds without the explicit UI confirmation.
//
// SAFE BY DEFAULT: unknown actions are rejected by executeAction() before the
// gate is reached. Only action types absent from DESTRUCTIVE_ACTIONS pass here.


import { DESTRUCTIVE_ACTIONS } from "./voiceActions";

const APPROVAL_TTL_MS = 30 * 1000;      // how long a confirmation stays valid
const MAX_PENDING_AGE_MS = 60 * 1000;   // how long an unconfirmed action stays pending

let _lastApprovedKey = null;
let _lastApprovedAct = null;
let _lastApprovedAt = 0;
let _lastPending = null;
let _listeners = new Set();

// Module-level listener pattern so the UI can react to "needs_confirm" without
// re-plumbing through props.
export function onConfirmGateEvent(handler) {
  _listeners.add(handler);
  return () => _listeners.delete(handler);
}
function _emit(evt) {
  for (const h of _listeners) {
    try { h(evt); } catch (e) { console.error("[voiceConfirmGate] listener error:", e); }
  }
}

function _stableActionValue(value) {
  if (Array.isArray(value)) return value.map(_stableActionValue);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.keys(value).sort().map(key => [key, _stableActionValue(value[key])])
    );
  }
  return value;
}

function _actionKey(act) {
  if (!act || typeof act !== "object") return "";
  const signature = Object.fromEntries(
    Object.keys(act).filter(key => key !== "__confirmed").sort()
      .map(key => [key, _stableActionValue(act[key])])
  );
  return JSON.stringify(signature);
}

export function describeAction(act) {
  if (!act || !act.type) return "perform that action";
  switch (act.type) {
    case "execute_trade": {
      const sym = act.symbol || "the symbol";
      const dir = act.direction || "CALL";
      const stake = act.stake ? `$${act.stake}` : "your default stake";
      return `place a ${dir} trade on ${sym} for ${stake}`;
    }
    case "activate_strategy": return `switch the active strategy to ${act.name || "a new strategy"}`;
    case "clear_symbols": return "unsubscribe from all symbols";
    case "clear_history": return "wipe the local trade history (irreversible)";
    case "create_strategy": return `create and save a new strategy${act.name ? ` named "${act.name}"` : ""}`;
    case "toggle_auto_trade": {
      const newVal = act.value === undefined ? "toggle" : act.value ? "ENABLE" : "DISABLE";
      return `${newVal === "toggle" ? "toggle" : newVal} auto-trading`;
    }
    case "set_stake": return `change the default stake to $${act.value}`;
    default: return `execute ${act.type}`;
  }
}

/**
 * Check whether an action may proceed. Destructive actions require __confirmed
 * = true AND a matching recently-approved key (within APPROVAL_TTL_MS).
 *
 * @returns {{execute:boolean, needsConfirm?:boolean, label?:string, summary?:string, reason?:string}}
 */
export function check(act) {
  if (!act || !act.type) {
    return { execute: false, reason: "Empty action." };
  }
  // Non-destructive actions pass through.
  if (!DESTRUCTIVE_ACTIONS.has(act.type)) {
    return { execute: true };
  }
  // Destructive + already confirmed by caller + same key within TTL -> execute.
  if (act.__confirmed) {
    const key = _actionKey(act);
    const now = Date.now();
    if (key === _lastApprovedKey && (now - _lastApprovedAt) <= APPROVAL_TTL_MS) {
      // One-shot use: clear so a second identical action requires re-confirm.
      _lastApprovedKey = null;
      _lastApprovedAct = null;
      _lastApprovedAt = 0;
      return { execute: true };
    }
    if (act.__confirmed && (now - _lastApprovedAt) > APPROVAL_TTL_MS) {
      return {
        execute: false,
        needsConfirm: true,
        label: describeAction(act),
        summary: `Confirmation expired (>${APPROVAL_TTL_MS/1000}s). Please re-confirm.`,
        reason: "expired",
      };
    }
  }
  // Destructive + not confirmed -> request confirmation.
  const summary = describeAction(act);
  // A fresh request invalidates any leftover token from an older action.
  _lastApprovedKey = null;
  _lastApprovedAct = null;
  _lastApprovedAt = 0;
  _lastPending = {
    act,
    summary,
    key: _actionKey(act),
    requestedAt: Date.now(),
  };
  _emit({ kind: "needs_confirm", act, summary });
  return {
    execute: false,
    needsConfirm: true,
    label: summary,
    summary: `Destructive action "${act.type}" blocked pending confirmation.`,
    reason: "needs_confirm",
  };
}

/**
 * Approve a pending destructive action. The caller must supply the exact
 * original action object that created the pending request; a matching payload
 * on a different/stale object is not sufficient. Sets the approval token so
 * the next confirmed dispatch with the same signature proceeds.
 */
export function approve(act) {
  const pending = getPending();
  if (!pending) return { ok: false, reason: "expired_or_missing" };
  const key = _actionKey(act);
  if (act !== pending.act || key !== pending.key) return { ok: false, reason: "action_mismatch" };
  _lastApprovedKey = key;
  _lastApprovedAct = act;
  _lastApprovedAt = Date.now();
  _lastPending = null;
  _emit({ kind: "approved", act });
  return { ok: true, validForMs: APPROVAL_TTL_MS };
}

/**
 * Reject a pending destructive action. Drops any cached approval.
 */
export function reject(act) {
  const pending = getPending();
  const key = act ? _actionKey(act) : null;
  if (pending && key && (act !== pending.act || key !== pending.key)) {
    return { ok: false, reason: "action_mismatch" };
  }
  if (!pending && key && _lastApprovedKey && (act !== _lastApprovedAct || key !== _lastApprovedKey)) {
    return { ok: false, reason: "action_mismatch" };
  }
  _lastApprovedKey = null;
  _lastApprovedAct = null;
  _lastApprovedAt = 0;
  _lastPending = null;
  _emit({ kind: "rejected", act });
  return { ok: true };
}

export function getPending() {
  if (!_lastPending) return null;
  if (Date.now() - _lastPending.requestedAt > MAX_PENDING_AGE_MS) {
    _lastPending = null;
    return null;
  }
  return _lastPending;
}

export function hasRecentApproval(act) {
  if (!act) return false;
  const key = _actionKey(act);
  return key === _lastApprovedKey && (Date.now() - _lastApprovedAt) <= APPROVAL_TTL_MS;
}

// Convenience: returns true if the act.type is destructive (used by UI to
// decide whether to draw the confirm banner).
export function isDestructive(actType) {
  return DESTRUCTIVE_ACTIONS.has(actType);
}
